/**
 * Weeds — Platform Admin страница справочника сорняков (AK-234).
 *
 * Тонкий wrapper над `ReferenceCatalogList` + `WeedEditorDrawer`.
 * Категории и жизненные циклы — мини-справочники
 * (reference.weed_categories / weed_life_cycles, migration 106): списки
 * фильтров и подписи бейджей приходят из API, а не из захардкоженных
 * констант. Delete = архивирование. Дизайн: ak226-reference-hub эталон.
 */
import { useCallback, useMemo, useState } from 'react';
import { Archive, GitMerge, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import {
  ReferenceMergeDialog,
  type MergeCandidate,
} from '@/components/reference/ReferenceMergeDialog';
import { WeedEditorDrawer } from './WeedEditorDrawer';
import {
  useArchiveWeed,
  useCreateWeed,
  useMergeWeeds,
  useUpdateWeed,
  useWeedCategories,
  useWeedLifeCycles,
  useWeedsInfinite,
} from './hooks/useCatalogQueries';
import {
  weedRefLabel,
  type ReferenceListQuery,
  type WeedListItem,
  type WeedPayload,
} from './types';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'записей';
  if (m10 === 1) return 'запись';
  if (m10 >= 2 && m10 <= 4) return 'записи';
  return 'записей';
}

export function PlatformAdminWeedsPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [lifeCycleFilter, setLifeCycleFilter] = useState<string>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<WeedListItem | null>(null);

  const categoriesQ = useWeedCategories();
  const categories = categoriesQ.data?.items ?? [];
  const lifeCyclesQ = useWeedLifeCycles();
  const lifeCycles = lifeCyclesQ.data?.items ?? [];

  const query: ReferenceListQuery = useMemo(
    () => ({
      search: search.trim() || undefined,
      // Бэкенд фильтрует по легаси-тексту (= code строки справочника).
      category:
        categoryFilter === 'all'
          ? undefined
          : categories.find((c) => c.id === categoryFilter)?.code,
      life_cycle:
        lifeCycleFilter === 'all'
          ? undefined
          : lifeCycles.find((c) => c.id === lifeCycleFilter)?.code,
    }),
    [search, categoryFilter, lifeCycleFilter, categories, lifeCycles],
  );

  const weedsQ = useWeedsInfinite(query);
  const createMut = useCreateWeed();
  const updateMut = useUpdateWeed();
  const archiveMut = useArchiveWeed();
  const mergeMut = useMergeWeeds();

  const items = weedsQ.data?.items ?? [];
  const total = weedsQ.data?.total ?? 0;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<WeedListItem | null>(null);

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (w: WeedListItem) => {
    setEditing(w);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: WeedPayload) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
      toast.success(`Сорняк «${payload.name_translations.ru}» сохранён`);
    } else {
      await createMut.mutateAsync(payload);
      toast.success(`Сорняк «${payload.name_translations.ru}» создан`);
    }
  };

  const pickerTargetCandidate = useMemo<MergeCandidate | null>(() => {
    if (!pickerTarget) return null;
    return {
      id: pickerTarget.id,
      label: pickerTarget.name_translations.ru ?? pickerTarget.latin_name ?? '—',
      sublabel: pickerTarget.latin_name ?? undefined,
    };
  }, [pickerTarget]);

  const pickerCandidates = useMemo<MergeCandidate[]>(
    () =>
      items
        .filter((w) => w.id !== pickerTarget?.id)
        .map((w) => ({
          id: w.id,
          label: w.name_translations.ru ?? w.latin_name ?? '—',
          sublabel: w.latin_name ?? undefined,
        })),
    [items, pickerTarget?.id],
  );

  const handlePickerPreview = useCallback(
    async (sourceId: string, targetId: string) => {
      const res = await mergeMut.mutateAsync({ sourceId, targetId, dryRun: true });
      return { totalRepointed: res.total_repointed, totalDeleted: res.total_deleted };
    },
    [mergeMut.mutateAsync],
  );

  const handlePickerConfirm = useCallback(
    async (sourceId: string, targetId: string) => {
      const label = pickerTarget?.name_translations.ru ?? 'эталон';
      try {
        await mergeMut.mutateAsync({ sourceId, targetId, dryRun: false });
        toast.success(`Поглощено в «${label}»`);
      } catch (err) {
        toast.error((err as Error).message);
        throw err;
      }
    },
    [mergeMut.mutateAsync, pickerTarget],
  );

  const handleArchive = async (w: WeedListItem) => {
    if (!window.confirm(`Архивировать «${w.name_translations.ru}»?`)) return;
    try {
      await archiveMut.mutateAsync(w.id);
      toast.success('Архивировано.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<WeedListItem>[] = [
    {
      key: 'name',
      header: 'Название (RU)',
      render: (w) => (
        <button
          type="button"
          onClick={() => handleEdit(w)}
          className="text-left font-medium text-foreground hover:text-primary"
        >
          {w.name_translations.ru ?? w.latin_name ?? '—'}
        </button>
      ),
    },
    {
      key: 'latin',
      header: 'Латинское',
      width: '190px',
      render: (w) => (
        <span className="text-sm italic text-muted-foreground">
          {w.latin_name ?? '—'}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Категория',
      width: '140px',
      render: (w) => (
        <Badge variant="secondary">
          {w.category_name ?? weedRefLabel(w.category, categories)}
        </Badge>
      ),
    },
    {
      key: 'life_cycle',
      header: 'Жизненный цикл',
      width: '150px',
      render: (w) => (
        <Badge variant="outline">
          {w.life_cycle_name ?? weedRefLabel(w.life_cycle, lifeCycles)}
        </Badge>
      ),
    },
    {
      key: 'eppo',
      header: 'EPPO',
      width: '90px',
      render: (w) => (
        <span className="font-mono text-xs uppercase text-muted-foreground">
          {w.eppo_code ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (w) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${w.name_translations.ru}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => handleEdit(w)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { setPickerTarget(w); setPickerOpen(true); }}>
              <GitMerge className="h-4 w-4" />
              Поглотить
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void handleArchive(w)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<WeedListItem>
        title="Сорняки"
        subtitle={`Системный справочник · read-only baseline для всех хозяйств · ${total} ${plural(total)}`}
        createLabel="Создать сорняк"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию, латыни или EPPO…"
        items={items}
        isLoading={weedsQ.isLoading}
        isError={weedsQ.isError}
        error={weedsQ.error ?? undefined}
        onRetry={() => weedsQ.refetch()}
        getRowKey={(w) => w.id}
        total={total}
        unitLabel="сорняков"
        hasMore={!!weedsQ.hasNextPage}
        loadingMore={weedsQ.isFetchingNextPage}
        onLoadMore={() => weedsQ.fetchNextPage()}
        columns={columns}
        emptyTitle="Пока нет ни одного сорняка"
        emptyHint="Создайте первую запись справочника сорняков — название, таксономия и жизненный цикл станут доступны всем хозяйствам."
        toolbarSlot={
          <>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все категории</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={lifeCycleFilter} onValueChange={setLifeCycleFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все циклы</SelectItem>
                {lifeCycles.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name_translations.ru ?? c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <WeedEditorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        weed={editing}
        categories={categories}
        lifeCycles={lifeCycles}
        onSubmit={handleSubmit}
      />

      <ReferenceMergeDialog
        mode="picker"
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        target={pickerTargetCandidate}
        candidates={pickerCandidates}
        entityAccusative="сорняк"
        onPreview={handlePickerPreview}
        onConfirm={handlePickerConfirm}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminWeedsPage;
